import { useState, useEffect } from 'react'
import { Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { fetchMerchantLogo, getMerchantPhotoUrl } from '../utils/merchantPhotoUtils'
import NotificationsPanel from './NotificationsPanel'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const DEFAULT_LOGO = '/petStoreDefault.svg'

// Nami lavender theme tokens (from the design handoff)
const COLOR = {
	bg: '#FFFFFF',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primarySoft: '#EDE9FB',
	primaryDeep: '#5F4EC2'
}

const FONT = "'Outfit', sans-serif"

function MerchantTopBar({
	merchant,
	notifications = [],
	unreadCount = 0,
	unreadMessageCount = 0,
	onApprove,
	onDecline,
	onOpenMessage,
	onMarkMessagesRead,
	onMenuClick,
	onNotificationsClick
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const [currentTime, setCurrentTime] = useState(dayjs())
	const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO)
	const [notifOpen, setNotifOpen] = useState(false)

	// Update time every second
	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTime(dayjs())
		}, 1000)

		return () => clearInterval(interval)
	}, [])

	// Fetch merchant logo
	useEffect(() => {
		async function loadLogo() {
			if (!merchant?.id) {
				setLogoUrl(DEFAULT_LOGO)
				return
			}

			const logoPhoto = await fetchMerchantLogo(merchant.id)
			if (logoPhoto) {
				const url = getMerchantPhotoUrl(logoPhoto)
				setLogoUrl(url || DEFAULT_LOGO)
			} else {
				setLogoUrl(DEFAULT_LOGO)
			}
		}

		loadLogo()
	}, [merchant?.id])

	// Format time based on merchant's timezone
	const formatDateTime = () => {
		const tz = merchant?.timezone || 'UTC'
		const now = currentTime.tz(tz)

		// Format: "September 23, 10:00 AM - PST"
		const date = now.format('MMMM D')
		const time = now.format('h:mm A')
		const timezoneAbbr = now.format('z')

		return `${date}, ${time} - ${timezoneAbbr}`
	}

	// Mobile — Nami "Clients & Pets · list" mobile topbar (hamburger + bell + store avatar)
	if (isMobile) {
		return (
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '8px',
				height: '64px',
				padding: '0 16px',
				bgcolor: COLOR.bg,
				boxSizing: 'border-box',
				flexShrink: 0
			}}>
				<IconButton
					onClick={onMenuClick}
					aria-label="Open menu"
					sx={{
						width: '40px !important',
						height: '40px !important',
						minWidth: '40px !important',
						minHeight: '40px !important',
						padding: '0 !important',
						ml: '-6px',
						borderRadius: '11px',
						color: COLOR.text,
						'&:hover': { bgcolor: COLOR.surface }
					}}
				>
					<svg width="21" height="21" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
						<path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
					</svg>
				</IconButton>

				<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
					{/* Notification bell */}
					<Box sx={{ position: 'relative' }}>
						<IconButton
							onClick={onNotificationsClick}
							aria-label="notifications"
							sx={{
								width: '38px !important',
								height: '38px !important',
								minWidth: '38px !important',
								minHeight: '38px !important',
								padding: '0 !important',
								border: `1.5px solid ${COLOR.border}`,
								bgcolor: '#FFFFFF',
								color: COLOR.muted,
								borderRadius: '11px',
								'&:hover': { bgcolor: COLOR.surface, color: COLOR.text }
							}}
						>
							<svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor"
								strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
								xmlns="http://www.w3.org/2000/svg">
								<path d="M8 2a4 4 0 014 4c0 3 1.2 4 1.2 4H2.8S4 9 4 6a4 4 0 014-4z" />
								<path d="M6.7 13a1.4 1.4 0 002.6 0" />
							</svg>
						</IconButton>
						{unreadCount > 0 && (
							<Box
								sx={{
									position: 'absolute',
									top: -4,
									right: -4,
									minWidth: 18,
									height: 18,
									px: '4px',
									borderRadius: '9px',
									bgcolor: COLOR.primary,
									color: '#FFFFFF',
									fontSize: '10.5px',
									fontWeight: 800,
									fontFamily: FONT,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									border: '2px solid #FFFFFF',
									pointerEvents: 'none'
								}}
							>
								{unreadCount > 99 ? '99+' : unreadCount}
							</Box>
						)}
					</Box>

					{/* Store logo */}
					<Box sx={{
						width: 38,
						height: 38,
						borderRadius: '50%',
						bgcolor: COLOR.primarySoft,
						flexShrink: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						overflow: 'hidden'
					}}>
						<img
							src={logoUrl}
							alt="Store Logo"
							style={{ width: '100%', height: '100%', objectFit: 'cover' }}
							onError={(e) => {
								if (e.target.src !== DEFAULT_LOGO) {
									e.target.src = DEFAULT_LOGO
								}
							}}
						/>
					</Box>
				</Box>
			</Box>
		)
	}

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '16px',
				padding: '0 20px',
				height: '62px',
				width: '100%',
				bgcolor: COLOR.bg,
				borderBottom: `1px solid ${COLOR.border}`,
				boxSizing: 'border-box'
			}}
		>
			{/* Left spacer (search lives here in the full design) */}
			<Box sx={{ flex: 1 }} />

			{/* Right-aligned actions: date/time · bell · separator · store identity */}
			<Box sx={{
				marginLeft: 'auto',
				display: 'flex',
				alignItems: 'center',
				gap: '14px'
			}}>

				{/* Notification bell — toggles the dropdown panel, shows unread count */}
				<Box sx={{ position: 'relative' }}>
					<IconButton
						onClick={() => setNotifOpen((v) => !v)}
						sx={{
							width: '38px',
							height: '38px',
							p: 0,
							bgcolor: notifOpen ? COLOR.primarySoft : COLOR.bg,
							border: notifOpen ? `1.5px solid ${COLOR.primary}` : `1.5px solid ${COLOR.border}`,
							borderRadius: '10px',
							color: notifOpen ? COLOR.primaryDeep : COLOR.muted,
							'&:hover': {
								bgcolor: notifOpen ? COLOR.primarySoft : COLOR.bg,
								color: notifOpen ? COLOR.primaryDeep : COLOR.text,
								borderColor: notifOpen ? COLOR.primary : COLOR.muted
							}
						}}
						aria-label="notifications"
					>
						{/* Bell icon matching the Nami topbar spec (16x16, 1.6 stroke) */}
						<svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor"
							strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
							xmlns="http://www.w3.org/2000/svg">
							<path d="M8 2a4 4 0 014 4c0 3 1.2 4 1.2 4H2.8S4 9 4 6a4 4 0 014-4z" />
							<path d="M6.7 13a1.4 1.4 0 002.6 0" />
						</svg>
					</IconButton>
					{unreadCount > 0 && (
						<Box
							sx={{
								position: 'absolute',
								top: -4,
								right: -4,
								minWidth: 18,
								height: 18,
								px: '4px',
								borderRadius: '9px',
								bgcolor: COLOR.primary,
								color: '#FFFFFF',
								fontSize: '10.5px',
								fontWeight: 800,
								fontFamily: FONT,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								border: '2px solid #FFFFFF',
								pointerEvents: 'none'
							}}
						>
							{unreadCount > 99 ? '99+' : unreadCount}
						</Box>
					)}
				</Box>

				{/* Separator */}
				<Box sx={{ width: '1px', height: '28px', bgcolor: COLOR.border, flexShrink: 0, mx: '3px' }} />

				{/* Store identity — avatar (logo) + store name, far right (topbar-user) */}
				<Box sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '9px',
					height: '44px',
					p: '0 7px 0 6px',
					borderRadius: '11px'
				}}>
					<Box
						sx={{
							width: '33px',
							height: '33px',
							borderRadius: '50%',
							bgcolor: COLOR.primarySoft,
							flexShrink: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							overflow: 'hidden'
						}}
					>
						<img
							src={logoUrl}
							alt="Store Logo"
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover'
							}}
							onError={(e) => {
								// Fallback to default logo if image fails to load
								if (e.target.src !== DEFAULT_LOGO) {
									e.target.src = DEFAULT_LOGO
								}
							}}
						/>
					</Box>
					<Typography
						sx={{
							fontFamily: FONT,
							fontWeight: 750,
							fontSize: '13.5px',
							lineHeight: 1.2,
							color: COLOR.text,
							whiteSpace: 'nowrap'
						}}
					>
						{merchant?.name || 'Store Name'}
					</Typography>
				</Box>
			</Box>

			{notifOpen && (
				<NotificationsPanel
					variant="desktop"
					notifications={notifications}
					unreadCount={unreadCount}
					unreadMessageCount={unreadMessageCount}
					onApprove={onApprove}
					onDecline={onDecline}
					onOpenMessage={(n) => {
						setNotifOpen(false)
						onOpenMessage && onOpenMessage(n)
					}}
					onMarkMessagesRead={onMarkMessagesRead}
					onClose={() => setNotifOpen(false)}
				/>
			)}
		</Box>
	)
}

export default MerchantTopBar
